import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Camera, Shield, Scale, Utensils, Droplets, Moon, ClipboardList, Heart, Apple, Beef, Salad, ChevronDown, Target, Users, Lightbulb, Flame, Fish, Clock, User, LayoutDashboard, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import BrandText from '@/components/Logo';
import LegalFooterLinks from '@/components/LegalFooterLinks';

// About Us Content Component (now includes Our Mission)
const AboutUsContent = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
      <div className="text-center mb-12">
        <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          About <span className="text-primary">Insul Nutrients</span>
        </h2>
      </div>

      {/* Welcome Section */}
      <div className="bg-card rounded-3xl border border-border/50 p-8 md:p-12 shadow-lg">
        <div className="flex items-start gap-6 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center flex-shrink-0">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome to Insul Nutrients
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Your personalised gateway to better health.
            </p>
          </div>
        </div>

        <div className="space-y-6 text-muted-foreground leading-relaxed">
          <p className="text-lg">
            Designed to simplify your wellness journey, our platform allows you to log your meals and build a detailed food diary with ease. Using advanced AI, each meal is analysed and broken down into its core nutrient components, giving you clear, actionable insights.
          </p>
          
          <p className="text-lg">
            With personalised feedback highlighting where your health can improve, Insul Nutrients empowers you to make smarter choices and take control of your wellbeing—one meal at a time.
          </p>
        </div>
      </div>

      {/* Our Mission Section */}
      <div className="bg-card rounded-3xl border border-border/50 p-8 md:p-12 shadow-lg space-y-8">
        <div className="flex items-start gap-6 mb-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center flex-shrink-0">
            <Target className="w-8 h-8 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Our Mission
            </h3>
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-lg text-muted-foreground leading-relaxed">
            For too long, wellness and good nutrition have felt out of reach for the time-poor individual. Balancing work, finances, and family is demanding enough—finding the time to prioritise your health can feel nearly impossible, and preventative care often becomes a luxury rather than a necessity.
          </p>
          
          <p className="text-lg text-muted-foreground leading-relaxed">
            Many existing platforms promise rapid lifestyle transformations, but in our experience, these approaches often lead to short-term results rather than lasting change. True health isn't built overnight—it's built through consistency.
          </p>
        </div>

        {/* Our Objective */}
        <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl p-8 border border-primary/20">
          <h3 className="text-2xl font-bold mb-4 text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Our Objective
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            At Insul Nutrients, our objective is simple: <strong className="text-foreground">small, achievable steps that lay the foundation for a lasting temple of health and wellbeing</strong>.
          </p>
          
          <div className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              By helping you log your meals, build a food diary, and receive AI-powered nutritional analysis, we provide clear insights into what you're eating and how it impacts your body.
            </p>
            
            <p className="text-muted-foreground leading-relaxed">
              With personalised feedback highlighting where your health can improve, each step forward builds progress, motivation, and momentum—empowering you to create sustainable, lifelong habits.
            </p>
          </div>
        </div>

        {/* Key Principles */}
        <div className="grid md:grid-cols-3 gap-6 pt-4">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <Scale className="w-8 h-8 text-green-600" />
            </div>
            <h4 className="font-bold text-lg mb-2">Consistency Over Intensity</h4>
            <p className="text-sm text-muted-foreground">Small daily actions create lasting change</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-blue-600" />
            </div>
            <h4 className="font-bold text-lg mb-2">Sustainable Habits</h4>
            <p className="text-sm text-muted-foreground">Building a temple of health, one meal at a time</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-purple-600" />
            </div>
            <h4 className="font-bold text-lg mb-2">Achievable Progress</h4>
            <p className="text-sm text-muted-foreground">Each step builds momentum and motivation</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// What We Offer Content Component
const WhatWeOfferContent = () => {
  const offerings = [
    {
      icon: Camera,
      title: "Easy Meal Logging",
      description: "Simply photograph your meals and let our platform do the rest. Building your food diary has never been easier—no manual data entry required.",
      gradient: "from-emerald-500 to-teal-500"
    },
    {
      icon: Lightbulb,
      title: "AI-Powered Nutritional Analysis",
      description: "Advanced AI instantly analyzes your meals, estimating macronutrients (protein, carbs, fats) and micronutrients with precision. Know exactly what you're eating.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: Target,
      title: "Personalised Health Insights",
      description: "Receive clear, actionable feedback highlighting nutritional gaps and areas for improvement. Track deficiencies and excesses over time.",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: ClipboardList,
      title: "Comprehensive Food Diary",
      description: "Build a detailed record of your meals with photos, descriptions, and nutritional breakdowns. Review your eating patterns and track progress over days, weeks, and months.",
      gradient: "from-amber-500 to-orange-500"
    },
    {
      icon: Heart,
      title: "Wellness Programs",
      description: "Subscribe to expert-designed wellness programs including Low Salt, Sugar Homeostasis, Lipid Target, and Fluid Balance programs.",
      gradient: "from-rose-500 to-red-500"
    },
    {
      icon: Scale,
      title: "Weekly Health Reports",
      description: "Get comprehensive weekly summaries of your nutritional intake, including daily averages and trends based on your ideal body weight.",
      gradient: "from-green-500 to-emerald-500"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
      <div className="text-center mb-12">
        <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          What We <span className="text-primary">Offer</span>
        </h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Everything you need to take control of your nutrition and build lasting healthy habits.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {offerings.map((offering, index) => (
          <motion.div
            key={offering.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 * index }}
            className="bg-card rounded-2xl border border-border/50 p-6 shadow-sm hover:shadow-lg transition-all duration-300 group"
          >
            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${offering.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
              <offering.icon className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {offering.title}
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              {offering.description}
            </p>
          </motion.div>
        ))}
      </div>

      {/* CTA Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="text-center bg-gradient-to-br from-primary/10 via-emerald-500/10 to-teal-500/10 rounded-3xl p-10 border border-primary/20 mt-12"
      >
        <h3 className="text-2xl md:text-3xl font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Ready to Transform Your <span className="text-primary">Nutrition</span>?
        </h3>
        <Button 
          className="rounded-full px-8 py-6 text-lg font-bold shadow-xl hover:shadow-2xl transition-all active:scale-95 bg-gradient-to-r from-primary to-emerald-500"
          onClick={() => window.location.href = '/signup'}
        >
          Get Started Free
        </Button>
      </motion.div>
    </motion.div>
  );
};

// Nutrition for Weight Loss Component
const NutritionForWeightLoss = () => {
  return (
    <div className="space-y-12">
      {/* Introduction */}
      <section className="space-y-4">
        <h2 className="text-3xl md:text-4xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
          There is no single "perfect" diet — and that's a good thing.
        </h2>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Whether it's keto, paleo, intermittent fasting, Mediterranean, vegan, low-carb, or low-fat — most structured eating plans can result in weight loss in the short term. The problem is that many of these diets are difficult to maintain long term, especially when they are overly restrictive, exclude entire food groups, or don't align with a person's lifestyle, culture, or food preferences.
        </p>
      </section>

      {/* The Truth About Diets */}
      <section className="bg-card rounded-2xl border border-border/50 p-8 space-y-4">
        <h3 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <ClipboardList className="w-6 h-6 text-secondary" />
          The Truth About Diets
        </h3>
        <p className="text-muted-foreground leading-relaxed">
          Most diets work temporarily because they reduce overall calorie intake. However:
        </p>
        <ul className="space-y-3 text-muted-foreground">
          <li className="flex items-start gap-3">
            <span className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0"></span>
            <span><strong className="text-foreground">Up to 80–95% of people</strong> who lose weight through a diet regain it within 1–5 years.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0"></span>
            <span>This isn't because people are lazy — it's because most diets ignore the underlying biology and behaviours that cause weight regain.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0"></span>
            <span><strong className="text-foreground">Sustainable change</strong> is not about a "diet" — it's about a way of eating that supports your goals, your body, and your lifestyle for the long haul.</span>
          </li>
        </ul>
        <div className="bg-primary/10 rounded-xl p-4 mt-4">
          <p className="text-lg font-semibold text-primary text-center">
            The best diet is the one you can stick to.
          </p>
        </div>
      </section>

      {/* What Works for Weight Loss */}
      <section className="space-y-6">
        <h3 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Scale className="w-6 h-6 text-primary" />
          What Works for Weight Loss?
        </h3>
        <p className="text-muted-foreground">
          While there is no one-size-fits-all plan, successful eating strategies often share the following features:
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { title: 'Calorie awareness', desc: 'Whether implicit (portion control) or explicit (calorie tracking)' },
            { title: 'Higher protein intake', desc: 'Helps preserve lean mass and promotes satiety' },
            { title: 'Reduced processed carbs', desc: 'Especially refined sugars and starches' },
            { title: 'High fibre content', desc: 'From vegetables, legumes, whole grains' },
            { title: 'Flexibility', desc: 'Allows for variety, cultural preferences, and social eating' },
            { title: 'Mindful eating', desc: 'Promotes slower, more conscious consumption' },
          ].map((item) => (
            <div key={`principle-${item.title}`} className="bg-muted/50 rounded-xl p-4">
              <h4 className="font-semibold text-foreground">{item.title}</h4>
              <p className="text-sm text-muted-foreground mt-1">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Thermic Effect of Food */}
      <section className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl p-8 space-y-4">
        <h3 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Beef className="w-6 h-6 text-primary" />
          The Thermic Effect of Food and Protein
        </h3>
        <p className="text-muted-foreground leading-relaxed">
          Did you know that your body burns calories just by digesting food? This is called the <strong className="text-foreground">thermic effect of food (TEF)</strong>. Protein has the highest TEF of all macronutrients, meaning your body uses more energy to digest and process it compared to carbs or fats.
        </p>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="bg-card rounded-xl p-4 text-center border border-green-500/30">
            <p className="text-3xl font-bold text-green-600">20-30%</p>
            <p className="text-sm text-muted-foreground mt-1">Protein calories burned</p>
          </div>
          <div className="bg-card rounded-xl p-4 text-center border border-blue-500/30">
            <p className="text-3xl font-bold text-blue-600">5-10%</p>
            <p className="text-sm text-muted-foreground mt-1">Carb calories burned</p>
          </div>
          <div className="bg-card rounded-xl p-4 text-center border border-yellow-500/30">
            <p className="text-3xl font-bold text-yellow-600">0-3%</p>
            <p className="text-sm text-muted-foreground mt-1">Fat calories burned</p>
          </div>
        </div>
        <p className="text-muted-foreground mt-4">
          That's one reason higher-protein diets can support weight loss — they help you feel fuller and slightly increase calorie burn through digestion.
        </p>
      </section>

      {/* How Much Protein */}
      <section className="space-y-6">
        <h3 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Apple className="w-6 h-6 text-secondary" />
          How Much Protein Do You Need?
        </h3>
        <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4">
          <ul className="space-y-3 text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
              <span>A good target for most people trying to lose weight is <strong className="text-foreground">1.2–1.6 grams per kg of body weight per day</strong></span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
              <span>For example, if you weigh 80 kg, aim for at least <strong className="text-foreground">100–130g of protein daily</strong></span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
              <span>As a rule of thumb: try to get <strong className="text-foreground">25–35g of protein at each main meal</strong></span>
            </li>
          </ul>
        </div>

        <h4 className="text-xl font-semibold mt-6">Easy Protein Examples</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { food: 'Eggs (2 large)', protein: '~12g' },
            { food: 'Chicken breast (100g)', protein: '~30g' },
            { food: 'Greek yogurt (170g)', protein: '~15g' },
            { food: 'Lentils (1 cup)', protein: '~18g' },
            { food: 'Whey protein', protein: '~20-25g' },
            { food: 'Tofu (100g)', protein: '~10g' },
            { food: 'Tuna can', protein: '~25g' },
          ].map((item) => (
            <div key={`protein-${item.food}`} className="bg-muted/50 rounded-xl p-3 text-center">
              <p className="text-sm font-medium">{item.food}</p>
              <p className="text-lg font-bold text-primary">{item.protein}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Divided Plate Approach */}
      <section className="bg-card rounded-2xl border border-border/50 p-8 space-y-6">
        <h3 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Salad className="w-6 h-6 text-primary" />
          Simple & Visual: The Divided Plate Approach
        </h3>
        <p className="text-muted-foreground">
          One of the easiest and most sustainable eating strategies is the plate model. It allows for flexibility while promoting balance:
        </p>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-green-500/10 rounded-2xl p-6 border border-green-500/30">
            <div className="text-4xl mb-3">🥗</div>
            <h4 className="text-xl font-bold text-green-700">Half of your plate</h4>
            <p className="font-semibold text-green-600 mb-2">Non-starchy vegetables</p>
            <p className="text-sm text-muted-foreground">
              Broccoli, spinach, capsicum, cucumber, tomato, cauliflower, etc.
            </p>
          </div>
          
          <div className="bg-amber-500/10 rounded-2xl p-6 border border-amber-500/30">
            <div className="text-4xl mb-3">🍗</div>
            <h4 className="text-xl font-bold text-amber-700">Quarter of your plate</h4>
            <p className="font-semibold text-amber-600 mb-2">Lean protein</p>
            <p className="text-sm text-muted-foreground">
              Chicken, fish, tofu, eggs, legumes, low-fat dairy
            </p>
          </div>
          
          <div className="bg-blue-500/10 rounded-2xl p-6 border border-blue-500/30">
            <div className="text-4xl mb-3">🍚</div>
            <h4 className="text-xl font-bold text-blue-700">Quarter of your plate</h4>
            <p className="font-semibold text-blue-600 mb-2">Smart carbohydrates</p>
            <p className="text-sm text-muted-foreground">
              Quinoa, sweet potato, brown rice, beans, oats
            </p>
          </div>
        </div>

        <div className="bg-muted/50 rounded-xl p-4 mt-4">
          <p className="font-semibold mb-2">This model helps you:</p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• Control portions without weighing or counting</li>
            <li>• Focus on satiety and nutrient density</li>
            <li>• Stay consistent in any setting (home, work, dining out)</li>
          </ul>
        </div>
      </section>

      {/* Simple Weight Loss Tips */}
      <section className="space-y-6">
        <h3 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Heart className="w-6 h-6 text-secondary" />
          Simple Weight Loss Tips That Work
        </h3>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-card rounded-xl p-5 border border-border/50 hover:shadow-md transition-shadow">
            <div className="text-2xl mb-2">🥤</div>
            <h4 className="font-semibold">Drink 500ml water before meals</h4>
            <p className="text-sm text-muted-foreground mt-1">Shown to reduce meal calorie intake by 13–20%</p>
          </div>
          
          <div className="bg-card rounded-xl p-5 border border-border/50 hover:shadow-md transition-shadow">
            <div className="text-2xl mb-2">🍽️</div>
            <h4 className="font-semibold">Slow down</h4>
            <p className="text-sm text-muted-foreground mt-1">Aim for at least 15–20 minutes per meal</p>
          </div>
          
          <div className="bg-card rounded-xl p-5 border border-border/50 hover:shadow-md transition-shadow">
            <div className="text-2xl mb-2">🙅‍♂️</div>
            <h4 className="font-semibold">Avoid distractions</h4>
            <p className="text-sm text-muted-foreground mt-1">Eat without phones or screens</p>
          </div>
          
          <div className="bg-card rounded-xl p-5 border border-border/50 hover:shadow-md transition-shadow">
            <div className="text-2xl mb-2">📏</div>
            <h4 className="font-semibold">Use smaller plates and bowls</h4>
            <p className="text-sm text-muted-foreground mt-1">Helps reduce unintentional overeating</p>
          </div>
          
          <div className="bg-card rounded-xl p-5 border border-border/50 hover:shadow-md transition-shadow">
            <div className="text-2xl mb-2">🛌</div>
            <h4 className="font-semibold">Prioritise sleep</h4>
            <p className="text-sm text-muted-foreground mt-1">Poor sleep increases hunger hormones and cravings</p>
          </div>
          
          <div className="bg-card rounded-xl p-5 border border-border/50 hover:shadow-md transition-shadow">
            <div className="text-2xl mb-2">📒</div>
            <h4 className="font-semibold">Track intake (short term)</h4>
            <p className="text-sm text-muted-foreground mt-1">Even 3–5 days can reveal helpful patterns</p>
          </div>
        </div>
      </section>

      {/* Is Restriction Helpful */}
      <section className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-2xl p-8 space-y-4">
        <h3 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Is Restriction Helpful?
        </h3>
        <p className="text-muted-foreground">
          Excessive or rigid restriction can backfire:
        </p>
        <ul className="space-y-2 text-muted-foreground">
          <li className="flex items-start gap-3">
            <span className="text-red-500 font-bold">✗</span>
            <span>It often leads to binge-restrict cycles</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-red-500 font-bold">✗</span>
            <span>Increases the psychological fixation on "forbidden" foods</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-red-500 font-bold">✗</span>
            <span>May disrupt metabolism and worsen long-term outcomes</span>
          </li>
        </ul>
        
        <div className="bg-card rounded-xl p-6 mt-6 border border-primary/30">
          <h4 className="font-bold text-lg text-primary mb-3">Instead, we focus on a structured flexibility model:</h4>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="text-green-500 font-bold">✓</span>
              <span>Set clear boundaries (e.g. limit alcohol to 1–2 days per week)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-500 font-bold">✓</span>
              <span>Include "planned treats" to reduce feelings of deprivation</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-500 font-bold">✓</span>
              <span>Avoid food guilt — focus on patterns, not perfection</span>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
};

// Home Content Component
const HomeContent = ({ navigate, isLoggedIn }) => {
  const features = [
    {
      icon: Camera,
      title: "Snap & Log",
      subtitle: "Effortless Meal Tracking",
      description: "Simply photograph your meals and let our platform do the rest. Building your food diary has never been easier.",
      gradient: "from-emerald-500 to-teal-500"
    },
    {
      icon: Lightbulb,
      title: "AI-Powered Insights",
      subtitle: "Smart Nutritional Analysis",
      description: "Advanced AI instantly analyzes your meals, estimating macros (protein, carbs, fats) and micronutrients with precision.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: Target,
      title: "Know Your Gaps",
      subtitle: "Personalized Feedback",
      description: "Receive clear, actionable insights showing where your nutrition may be lacking—empowering smarter food choices.",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: Heart,
      title: "Wellness Programs",
      subtitle: "Guided Health Journeys",
      description: "Subscribe to expert-designed programs tailored to your wellness goals—from nutrition optimization to peak performance.",
      gradient: "from-rose-500 to-orange-500"
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center space-y-8 mb-20"
      >
        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
          Your Journey to Better Health Starts Here
        </div>
        
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Your Pathway to
          <br />
          <span className="bg-gradient-to-r from-primary via-emerald-500 to-teal-500 bg-clip-text text-transparent">
            Wellness
          </span>
        </h1>
        
        <p className="text-lg md:text-xl leading-relaxed max-w-3xl mx-auto text-muted-foreground">
          Transform your relationship with food through AI-powered nutritional insights. 
          Track meals effortlessly, understand your nutrition, and achieve lasting wellness—one meal at a time.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
          <Button 
            data-testid="hero-cta-button"
            className="rounded-full px-10 py-7 text-xl font-bold shadow-2xl hover:shadow-3xl transition-all active:scale-95 bg-gradient-to-r from-primary to-emerald-500"
            onClick={() => navigate(isLoggedIn ? '/dashboard' : '/signup')}
          >
            {isLoggedIn ? 'Go to Dashboard' : 'Start Your Journey'}
          </Button>
          {!isLoggedIn && (
            <Button 
              variant="outline"
              className="rounded-full px-8 py-6 text-lg font-medium"
              onClick={() => navigate('/login')}
            >
              Already a member? Sign In
            </Button>
          )}
        </div>
      </motion.div>

      {/* Hero Illustration */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-20 relative"
      >
        <div className="relative max-w-4xl mx-auto">
          {/* Decorative gradient blob */}
          <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-emerald-500/20 to-teal-500/20 rounded-[2rem] blur-2xl opacity-60" />
          
          {/* Main image container */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/20">
            <img 
              src="/images/landing-hero.jpg" 
              alt="Woman taking a photo of her meal with smartphone"
              className="w-full h-[300px] md:h-[400px] object-cover"
            />
            
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            
            {/* Floating badges */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center">
                <Camera className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">Snap a photo</p>
                <p className="text-xs text-gray-500">AI does the rest</p>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1 }}
              className="absolute bottom-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">Get insights</p>
                <p className="text-xs text-gray-500">Know your nutrition</p>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Features Section */}
      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="mb-20"
      >
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Everything You Need for <span className="text-primary">Nutritional Success</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Powerful features designed to make healthy eating simple, insightful, and sustainable.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="bg-card text-card-foreground rounded-3xl border border-border/50 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden p-8 group"
            >
              <div className="flex items-start gap-5">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{feature.subtitle}</p>
                  <h3 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* How It Works Section */}
      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="mb-20"
      >
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How It <span className="text-primary">Works</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Three simple steps to transform your nutrition journey.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { step: "1", title: "Snap Your Meal", description: "Take a photo of your food with a utensil for scale reference", icon: Camera },
            { step: "2", title: "Get AI Analysis", description: "Our AI instantly identifies foods and calculates nutritional content", icon: Lightbulb },
            { step: "3", title: "Track Progress", description: "View trends, identify gaps, and optimize your nutrition over time", icon: Target }
          ].map((item, index) => (
            <motion.div
              key={item.step}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 * index }}
              className="text-center"
            >
              <div className="relative inline-block mb-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <item.icon className="w-10 h-10 text-primary" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">
                  {item.step}
                </div>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
              <p className="text-muted-foreground">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="text-center bg-gradient-to-br from-primary/10 via-emerald-500/10 to-teal-500/10 rounded-3xl p-12 border border-primary/20"
      >
        <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Ready to Begin Your <span className="text-primary">Wellness Journey</span>?
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
          Start tracking, start understanding, start thriving.
        </p>
        <Button 
          className="rounded-full px-10 py-7 text-xl font-bold shadow-2xl hover:shadow-3xl transition-all active:scale-95 bg-gradient-to-r from-primary to-emerald-500"
          onClick={() => navigate(isLoggedIn ? '/dashboard' : '/signup')}
        >
          {isLoggedIn ? 'Go to Dashboard' : 'Get Started Free'}
        </Button>
      </motion.div>
    </>
  );
};

// Dropdown Menu Component
const WellnessDropdown = ({ activeTab, setActiveTab }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isWellnessActive = activeTab === 'nutrition';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        data-testid="wellness-tab"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1 px-6 py-3 font-semibold text-sm transition-all border-b-2 -mb-[1px] ${
          isWellnessActive
            ? 'border-primary text-primary' 
            : 'border-transparent text-muted-foreground hover:text-foreground'
        }`}
      >
        Wellness
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed mt-2 w-56 bg-card rounded-xl shadow-xl border border-border z-[100] overflow-hidden"
            style={{
              top: buttonRef.current ? buttonRef.current.getBoundingClientRect().bottom + 8 : 0,
              left: buttonRef.current ? buttonRef.current.getBoundingClientRect().left : 0,
            }}
          >
            <button
              data-testid="nutrition-dropdown-item"
              onClick={() => {
                setActiveTab('nutrition');
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-3.5 text-sm hover:bg-muted transition-colors flex items-center gap-3 ${
                activeTab === 'nutrition' ? 'bg-primary/10 text-primary font-medium' : ''
              }`}
            >
              <Scale className="w-4 h-4" />
              Nutrition for Weight Loss
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('home');

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-transparent">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex justify-between items-center">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => setActiveTab('home')}
        >
          <span className="text-2xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>INSUL NUTRIENTS</span>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex gap-4 items-center"
        >
          {user && token ? (
            /* Logged in - show user account button */
            <Button 
              data-testid="user-account-button"
              className="rounded-full px-6 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 bg-gradient-to-r from-primary to-[#8FD14F] flex items-center gap-2"
              onClick={() => navigate('/dashboard')}
            >
              <User className="w-5 h-5" />
              <span className="max-w-[200px] truncate">{user.email}</span>
              <LayoutDashboard className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            /* Not logged in - show login button */
            <Button 
              data-testid="login-button"
              className="rounded-full px-10 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all active:scale-95 bg-gradient-to-r from-primary to-[#8FD14F]"
              onClick={() => navigate('/login')}
            >
              Login
            </Button>
          )}
        </motion.div>
      </nav>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-2 border-b border-border/50 pb-0 overflow-x-auto">
          <button
            data-testid="home-tab"
            onClick={() => setActiveTab('home')}
            className={`px-6 py-3 font-semibold text-sm transition-all border-b-2 -mb-[1px] whitespace-nowrap ${
              activeTab === 'home' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Home
          </button>
          <button
            data-testid="about-us-tab"
            onClick={() => setActiveTab('about')}
            className={`px-6 py-3 font-semibold text-sm transition-all border-b-2 -mb-[1px] whitespace-nowrap ${
              activeTab === 'about' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            About Us
          </button>
          <button
            data-testid="what-we-offer-tab"
            onClick={() => setActiveTab('offer')}
            className={`px-6 py-3 font-semibold text-sm transition-all border-b-2 -mb-[1px] whitespace-nowrap ${
              activeTab === 'offer' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            What We Offer
          </button>
          <WellnessDropdown activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <AnimatePresence mode="wait">
          {activeTab === 'home' && (
            <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <HomeContent navigate={navigate} />
            </motion.div>
          )}
          {activeTab === 'about' && (
            <motion.div key="about" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <AboutUsContent />
            </motion.div>
          )}
          {activeTab === 'offer' && (
            <motion.div key="offer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <WhatWeOfferContent />
            </motion.div>
          )}
          {activeTab === 'nutrition' && (
            <motion.div key="nutrition" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <NutritionForWeightLoss />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center border-t border-border/50 mt-24">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">© 2026 Track your nutrition, visually.</p>
          <LegalFooterLinks />
          <div className="flex items-center gap-6">
            <Link 
              to="/contact-us" 
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 font-medium"
              data-testid="contact-us-link"
            >
              <Mail className="w-4 h-4" />
              Contact Us
            </Link>
            <Link 
              to="/admin-login" 
              className="text-sm text-muted-foreground hover:text-foreground hover:underline flex items-center gap-1"
              data-testid="footer-admin-link"
            >
              <Shield className="w-4 h-4" />
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
